import React, { useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { Node } from "@tiptap/core";

import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { wordPrintDocument } from "./WordEditorHalper";
import {
  Table,
  TableRow,
  TableCell,
  TableHeader,
} from "@tiptap/extension-table";

import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaStrikethrough,
  FaListUl,
  FaListOl,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaAlignJustify,
  FaLink,
  FaImage,
  FaUndo,
  FaRedo,
  FaTable,
  FaPlus,
  FaMinus,
  FaEraser,
  FaPrint,
  FaFileAlt,
} from "react-icons/fa";

import "./WordEditor.css";

/* =========================================================
   PAGE BREAK
========================================================= */

const PageBreak = Node.create({
  name: "pageBreak",

  group: "block",

  atom: true,

  parseHTML() {
    return [
      {
        tag: "div[data-page-break]",
      },
    ];
  },

  renderHTML() {
    return [
      "div",
      {
        "data-page-break": "",
        class: "page-break",
      },
    ];
  },
});

/* =========================================================
   COMPONENT
========================================================= */

const WordEditor = ({ onChange, initialContent = "" }) => {
  const [linkUrl, setLinkUrl] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),

      PageBreak,

      Underline,

      TextStyle,

      Color,

      Highlight.configure({
        multicolor: true,
      }),

      TextAlign.configure({
        types: ["heading", "paragraph", "image"],
      }),

      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),

      Image.configure({
        inline: false,
        allowBase64: true,
      }),

      Table.configure({
        resizable: true,
      }),

      TableRow,
      TableHeader,
      TableCell,
    ],

    content:
      initialContent ||
      `
        <h1>Paste the Message here</h1>

        <p>
          Copy content from Website or ms Word 
        </p>

        <p>
         or Start writing your document here...
        </p>
      `,

    editorProps: {
      attributes: {
        class: "word-editor-content",
      },

      handlePaste(view, event) {
        const clipboardData = event.clipboardData;

        if (!clipboardData) {
          return false;
        }

        const html = clipboardData.getData("text/html");

        if (html) {
          return false;
        }

        return false;
      },
    },

    onUpdate({ editor }) {
      const html = editor.getHTML();
      const json = editor.getJSON();
      const text = editor.getText();

      if (onChange) {
        onChange({
          html,
          json,
          text,
        });
      }
    },
  });

  /* =========================================================
     LINK
  ========================================================= */

  const addLink = () => {
    if (!editor) return;

    if (!linkUrl) {
      editor.chain().focus().unsetLink().run();
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({
        href: linkUrl,
      })
      .run();

    setLinkUrl("");
  };

  /* =========================================================
     IMAGE
  ========================================================= */

  const addImage = () => {
    if (!editor) return;

    const url = window.prompt("Enter image URL");

    if (!url) return;

    editor
      .chain()
      .focus()
      .setImage({
        src: url,
      })
      .run();
  };

  /* =========================================================
     TABLE
  ========================================================= */

  const addTable = () => {
    if (!editor) return;

    editor
      .chain()
      .focus()
      .insertTable({
        rows: 3,
        cols: 3,
        withHeaderRow: true,
      })
      .run();
  };

  /* =========================================================
     PAGE BREAK
  ========================================================= */

  const addPageBreak = () => {
    if (!editor) return;

    editor
      .chain()
      .focus()
      .insertContent({
        type: "pageBreak",
      })
      .run();
  };

  /* =========================================================
     PRINT
  ========================================================= */

  /* =========================================================
     LOADING
  ========================================================= */

  if (!editor) {
    return null;
  }

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="word-editor-wrapper">
      {/* =====================================================
          MAIN TOOLBAR
      ===================================================== */}

      <div className="word-editor-toolbar">
        <ToolbarButton
          icon={<FaUndo />}
          title="Undo"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        />

        <ToolbarButton
          icon={<FaRedo />}
          title="Redo"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        />

        <ToolbarDivider />

        {/* Heading */}

        <select
          className="editor-select"
          value={
            editor.isActive("heading")
              ? editor.getAttributes("heading").level
              : "paragraph"
          }
          onChange={(e) => {
            const value = e.target.value;

            if (value === "paragraph") {
              editor.chain().focus().setParagraph().run();
            } else {
              editor
                .chain()
                .focus()
                .toggleHeading({
                  level: Number(value),
                })
                .run();
            }
          }}
        >
          <option value="paragraph">Normal</option>

          <option value="1">Heading 1</option>

          <option value="2">Heading 2</option>

          <option value="3">Heading 3</option>

          <option value="4">Heading 4</option>

          <option value="5">Heading 5</option>

          <option value="6">Heading 6</option>
        </select>

        <ToolbarDivider />

        {/* Formatting */}

        <ToolbarButton
          icon={<FaBold />}
          title="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />

        <ToolbarButton
          icon={<FaItalic />}
          title="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />

        <ToolbarButton
          icon={<FaUnderline />}
          title="Underline"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        />

        <ToolbarButton
          icon={<FaStrikethrough />}
          title="Strike"
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        />

        <ToolbarButton
          icon={<FaEraser />}
          title="Clear formatting"
          onClick={() =>
            editor.chain().focus().clearNodes().unsetAllMarks().run()
          }
        />

        <ToolbarDivider />

        {/* Alignment */}

        <ToolbarButton
          icon={<FaAlignLeft />}
          title="Align left"
          active={editor.isActive({
            textAlign: "left",
          })}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
        />

        <ToolbarButton
          icon={<FaAlignCenter />}
          title="Align center"
          active={editor.isActive({
            textAlign: "center",
          })}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
        />

        <ToolbarButton
          icon={<FaAlignRight />}
          title="Align right"
          active={editor.isActive({
            textAlign: "right",
          })}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
        />

        <ToolbarButton
          icon={<FaAlignJustify />}
          title="Justify"
          active={editor.isActive({
            textAlign: "justify",
          })}
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}
        />

        <ToolbarDivider />

        {/* Lists */}

        <ToolbarButton
          icon={<FaListUl />}
          title="Bullet list"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />

        <ToolbarButton
          icon={<FaListOl />}
          title="Numbered list"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />

        <ToolbarDivider />

        {/* Link */}

        {/* <input
          type="text"
          className="link-input"
          placeholder="https://..."
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
        />

        <ToolbarButton icon={<FaLink />} title="Add link" onClick={addLink} />

        <ToolbarButton
          icon={<FaImage />}
          title="Add image"
          onClick={addImage}
        /> */}

        <ToolbarDivider />

        {/* Table */}

        <ToolbarButton
          icon={<FaTable />}
          title="Insert table"
          onClick={addTable}
        />

        <ToolbarButton
          icon={<FaPlus />}
          title="Add column"
          onClick={() => editor.chain().focus().addColumnAfter().run()}
          disabled={!editor.can().addColumnAfter()}
        />

        <ToolbarButton
          icon={<FaMinus />}
          title="Delete column"
          onClick={() => editor.chain().focus().deleteColumn().run()}
          disabled={!editor.can().deleteColumn()}
        />

        <ToolbarDivider />

        {/* Page */}

        <ToolbarButton
          icon={<FaFileAlt />}
          title="Insert Page Break"
          onClick={addPageBreak}
        />

        {/* PRINT */}

        <ToolbarButton
          icon={<FaPrint />}
          title="Print"
          onClick={() => wordPrintDocument(editor)}
        />
      </div>

      {/* =====================================================
          TABLE TOOLBAR
      ===================================================== */}

      <div className="table-toolbar">
        <button
          type="button"
          onClick={() => editor.chain().focus().addRowAfter().run()}
        >
          + Row
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().deleteRow().run()}
        >
          − Row
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().mergeCells().run()}
        >
          Merge Cells
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().splitCell().run()}
        >
          Split Cell
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeaderRow().run()}
        >
          Header Row
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().deleteTable().run()}
        >
          Delete Table
        </button>
      </div>

      {/* =====================================================
          DOCUMENT
      ===================================================== */}

      <div
        className="word-document-container"
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

/* =========================================================
   TOOLBAR BUTTON
========================================================= */

const ToolbarButton = ({ icon, title, onClick, active, disabled }) => {
  return (
    <button
      type="button"
      className={`toolbar-button ${active ? "active" : ""}`}
      title={title}
      onClick={onClick}
      disabled={disabled}
    >
      {icon}
    </button>
  );
};

/* =========================================================
   TOOLBAR DIVIDER
========================================================= */

const ToolbarDivider = () => {
  return <div className="toolbar-divider" />;
};

export default WordEditor;
